import type { BoundingBox } from '@eye-able/types';
import type { RoundedCrop } from './types';
import { PREVIEW_MARGIN, ENLARGED_MARGIN } from './constants';

/**
 * Checks if a bounding box object is valid (has x, y, width, and height, and
 * width/height are nonzero).
 *
 * @param boundingBox The bounding box to validate.
 * @returns `true` if the bounding box is valid, `false` otherwise.
 */
export function validateBoundingBoxValues(
  boundingBox?: BoundingBox | null,
): boundingBox is BoundingBox {
  if (!boundingBox) return false;

  const { width, height } = boundingBox;

  return !!(
    width &&
    height &&
    Object.hasOwn(boundingBox, 'x') &&
    Object.hasOwn(boundingBox, 'y')
  );
}

interface BuildCropParams {
  baseUrl?: string | null;
  cropWidth: number;
  cropHeight: number;
  cropX: number;
  cropY: number;
  targetWidth?: number;
}

/**
 * Builds an image URL with an explicit crop rectangle plus optional resize.
 *
 * @param params The crop rectangle and optional resize width.
 * @returns The processed image URL, or `null` if no `baseUrl` is provided.
 * @see https://docs.bunny.net/docs/stream-image-processing
 */
export function buildCropUrl({
  baseUrl,
  cropWidth,
  cropHeight,
  cropX,
  cropY,
  targetWidth,
}: BuildCropParams): string | null {
  if (!baseUrl) return null;

  try {
    const url = new URL(baseUrl);
    const roundedCropWidth = Math.max(Math.round(cropWidth), 1);
    const roundedCropHeight = Math.max(Math.round(cropHeight), 1);
    const roundedCropX = Math.max(Math.round(cropX), 0);
    const roundedCropY = Math.max(Math.round(cropY), 0);

    url.searchParams.set(
      'crop',
      `${roundedCropWidth},${roundedCropHeight},${roundedCropX},${roundedCropY}`,
    );

    if (targetWidth) {
      const roundedTargetWidth = Math.max(Math.round(targetWidth), 1);
      const roundedTargetHeight = Math.max(
        Math.round((roundedCropHeight / roundedCropWidth) * roundedTargetWidth),
        1,
      );
      url.searchParams.set('width', String(roundedTargetWidth));
      url.searchParams.set('height', String(roundedTargetHeight));
    }

    return url.toString();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to build image URL', error);
    return baseUrl;
  }
}

/**
 * Derives a crop rectangle around a bounding box with padding.
 * Width/height are rounded; origin is floored.
 */
export function buildRoundedCrop(
  boundingBox: BoundingBox,
  padding: number,
): RoundedCrop {
  const { x, y, width, height } = boundingBox;
  const cropWidth = Math.max(Math.round(width + 2 * padding), 1);
  const cropHeight = Math.max(Math.round(height + 2 * padding), 1);
  const cropX = Math.max(Math.floor(x - padding), 0);
  const cropY = Math.max(Math.floor(y - padding), 0);

  return { cropWidth, cropHeight, cropX, cropY };
}

/**
 * Clamps a crop rectangle to fit within the image bounds by shifting the
 * origin when the crop would overflow.
 */
export function clampCropToImage(
  crop: RoundedCrop,
  image?: HTMLImageElement | null,
): RoundedCrop {
  if (!image) return crop;

  const maxCropX = Math.max(image.naturalWidth - crop.cropWidth, 0);
  const maxCropY = Math.max(image.naturalHeight - crop.cropHeight, 0);

  return {
    ...crop,
    cropX: Math.min(Math.max(crop.cropX, 0), maxCropX),
    cropY: Math.min(Math.max(crop.cropY, 0), maxCropY),
  };
}

/**
 * Derive rounded crop rectangles for preview and enlarged views.
 * Expects a validated bounding box.
 */
export function getRoundedCrops(boundingBox: BoundingBox): {
  previewCrop: RoundedCrop;
  enlargedCrop: RoundedCrop;
} {
  const previewCrop = buildRoundedCrop(boundingBox, PREVIEW_MARGIN);
  const enlargedCrop = buildRoundedCrop(boundingBox, ENLARGED_MARGIN);

  return { previewCrop, enlargedCrop };
}
